// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import DynamicDropdown from "@insite/mobius/DynamicDropdown";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CreateAccountFormInfoCollectionModel } from "@keller/App/Services/CreateAccountService";
import { AccountFormState } from "@keller/App/Widgets/KscCreateAccount/CreateAccount.form.module";
import { xMask, xScreen } from "@keller/XDL/Utilities";
import { CreateForm } from "@keller/XDL/Utilities/form";
import React, { FC, useState } from "react";
import { css } from "styled-components";
import PasswordRequirementValidator from "@keller/App/Components/Common/PasswordRequirements";

interface OwnProps {
    accountData?: CreateAccountFormInfoCollectionModel;
    accountForm?: CreateForm<AccountFormState>;
}

interface CreateAccountUserFormStyles {
    branchGridItem?: GridItemProps;
    firstNameGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    phoneNumberGridItem?: GridItemProps;
    searchFieldWrapper?: InjectableCss;
    branchSelectField?: SelectPresentationProps;
    textField?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    passwordWrapperGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    confirmPasswordGridItem?: GridItemProps;
    passwordIcon?: IconPresentationProps;
    requirements?: TypographyProps;
    confirmPasswordIcon?: IconPresentationProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
}

const createAccountUserFormStyles: CreateAccountUserFormStyles = {
    branchGridItem: {
        width: 12,
    },
    searchFieldWrapper: {
        css: css`
            width: 100%;
            max-width: 450px;
            margin-bottom: 10px;
            ${xScreen("lte", "md")} {
                max-width: 100%;
            }
        `,
    },
    firstNameGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    lastNameGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    emailGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    userNameGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    phoneNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    phoneNumberTextField: {
        cssOverrides: {
            formInputWrapper: css`
                input {
                    &:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                        &::placeholder {
                            font-style: normal;
                        }
                    }
                }
            `,
        },
    },
    passwordWrapperGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    confirmPasswordGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    requirements: {
        size: 12,
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            margin-top: 12px;
        `,
    },
    confirmPasswordTextField: {
        labelProps: {
            css: css`
                margin-top: 12px;
                line-height: 13px;
                min-height: unset;
            `,
        },
    },
};

const styles = createAccountUserFormStyles;
const CreateAccountUserForm: FC<OwnProps> = ({ accountData, accountForm }) => {
    const accountSettings = useAppSelector(state => getSettingsCollection(state).accountSettings);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    if (!accountForm) {
        return null;
    }

    const branchOptions = accountData?.warehouses?.map(warehouse => ({
        optionText: warehouse.name,
        optionValue: warehouse.id,
    }));
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { fields, isSubmitted } = accountForm?.state;

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
    } = accountSettings;

    return (
        <>
            <GridItem {...styles.branchGridItem}>
                {branchOptions && branchOptions.length > 0 && (
                    <StyledWrapper {...styles.searchFieldWrapper}>
                        <DynamicDropdown
                            options={branchOptions}
                            onSelectionChange={value => accountForm?.onFieldChange(fields.defaultWarehouseId, value)}
                            error={isSubmitted && fields.defaultWarehouseId.error}
                            {...fields.defaultWarehouseId.attrs}
                            {...styles.branchSelectField}
                        />
                    </StyledWrapper>
                )}
            </GridItem>
            <GridItem {...styles.firstNameGridItem}>
                <TextField
                    value={fields.firstName.value}
                    onChange={e => accountForm?.onFieldChange(fields.firstName, e.currentTarget.value)}
                    error={isSubmitted && fields.firstName.error}
                    {...fields.firstName.attrs}
                    {...styles.textField}
                />
            </GridItem>
            <GridItem {...styles.lastNameGridItem}>
                <TextField
                    value={fields.lastName.value}
                    onChange={e => accountForm?.onFieldChange(fields.lastName, e.currentTarget.value)}
                    error={isSubmitted && fields.lastName.error}
                    {...fields.lastName.attrs}
                    {...styles.textField}
                />
            </GridItem>
            {!accountSettings.useEmailAsUserName && (
                <GridItem {...styles.userNameGridItem}>
                    <TextField
                        value={fields.userName.value}
                        onChange={e => accountForm?.onFieldChange(fields.userName, e.currentTarget.value)}
                        error={isSubmitted && fields.userName.error}
                        {...fields.userName.attrs}
                        {...styles.textField}
                    />
                </GridItem>
            )}
            <GridItem {...styles.emailGridItem}>
                <TextField
                    value={fields.email.value}
                    onChange={e => accountForm?.onFieldChange(fields.email, e.currentTarget.value)}
                    error={isSubmitted && fields.email.error}
                    {...fields.email.attrs}
                    {...styles.textField}
                />
            </GridItem>
            <GridItem {...styles.phoneNumberGridItem}>
                <TextField
                    value={xMask.phoneNumberUS(fields.phone.value || "")}
                    onChange={e => accountForm?.onFieldChange(fields.phone, e.currentTarget.value)}
                    error={isSubmitted && fields.phone.error}
                    {...fields.phone.attrs}
                    {...styles.phoneNumberTextField}
                />
            </GridItem>
            <GridItem {...styles.passwordWrapperGridItem}>
                <GridContainer gap={23}>
                    <GridItem {...styles.passwordGridItem}>
                        <TextField
                            type={showPassword ? undefined : "password"}
                            iconProps={{ ...styles.passwordIcon, src: showPassword ? EyeOff : Eye }}
                            iconClickableProps={{
                                onClick: () => {
                                    setShowPassword(!showPassword);
                                },
                                type: "button",
                            }}
                            clickableText={fields.password.value}
                            value={fields.password.value}
                            onChange={e => accountForm?.onFieldChange(fields.password, e.currentTarget.value)}
                            error={isSubmitted && fields.password.error}
                            {...fields.password.attrs}
                            {...styles.textField}
                        />
                        <TextField
                            type={showConfirmPassword ? undefined : "password"}
                            iconProps={{
                                ...styles.confirmPasswordIcon,
                                src: showConfirmPassword ? EyeOff : Eye,
                            }}
                            iconClickableProps={{
                                onClick: () => {
                                    setShowConfirmPassword(!showConfirmPassword);
                                },
                                type: "button",
                            }}
                            clickableText={fields.confirmPassword.value}
                            value={fields.confirmPassword.value}
                            onChange={e => accountForm?.onFieldChange(fields.confirmPassword, e.currentTarget.value)}
                            error={isSubmitted && fields.confirmPassword.error}
                            {...fields.confirmPassword.attrs}
                            {...styles.confirmPasswordTextField}
                        />
                    </GridItem>
                    <GridItem {...styles.passwordGridItem}>
                        <PasswordRequirementValidator
                            password={fields.password.value}
                            passwordMinimumLength={passwordMinimumLength}
                            passwordRequiresDigit={passwordRequiresDigit}
                            passwordRequiresLowercase={passwordRequiresLowercase}
                            passwordRequiresUppercase={passwordRequiresUppercase}
                            passwordRequiresSpecialCharacter={passwordRequiresSpecialCharacter}
                        />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </>
    );
};

export default CreateAccountUserForm;
